import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { PaymentRegistrationSuccessState } from '@features/paymentReconciliationModal/enums/paymentRegistrationSuccessState'
import { useCreateSubjectsAndBankLinesReconciliation } from '@features/paymentReconciliationModal/hooks'
import { convertSubjectsIdsToReconciliationSubjects } from '@features/paymentReconciliationModal/utils/convertSubjectsIdsToReconciliationSubjects'
import { notifyPaymentRegistrationError } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import { notifyPaymentRegistrationSuccess } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationSuccess'
import { BankMatchUiFlowValue, UiSourceValue } from '@features/reconciliation/enums'
import {
  useBankLineSubjectAssociations,
  useDeleteBankLineSubjectAssociations,
} from '@modules-deprecated/bankReconciliation/hooks'
import { DifferenceType } from '@modules-deprecated/bankReconciliation/types'

export interface UseReconcileInvoiceProps {
  invoiceId?: string
  organizationId?: string | null
  differenceType?: DifferenceType
  crossCurrencyPaymentAmount?: number | null
  bankLineId?: string
  selectedDifferenceType?: DifferenceType
  onSuccess?: () => void
  isAutoMatch?: boolean
  invoiceType?: InvoiceType.Invoice | InvoiceType.ExternalInvoice
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

export const useReconcileInvoice = ({
  invoiceId,
  onSuccess,
  differenceType,
  organizationId,
  crossCurrencyPaymentAmount,
  bankLineId,
  isAutoMatch,
  invoiceType = InvoiceType.Invoice,
  uiSource,
  uiFlow,
}: UseReconcileInvoiceProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { bankLineSubjectAssociations: invoiceSubjectAssociations } = useBankLineSubjectAssociations({
    subjectId: invoiceId,
    subjectType: invoiceType,
  })
  const { t } = useTranslation()

  const { create: createInvoicesAndBankLinesReconciliation } = useCreateSubjectsAndBankLinesReconciliation({
    onSuccess: () => {
      onSuccess?.()
      notifyPaymentRegistrationSuccess(PaymentRegistrationSuccessState.RegisteredAndReconciled, t)
      invalidateInvoice(invoiceId)
      invalidateInvoices()
      setIsProcessing(false)
    },
    onError: () => {
      setIsProcessing(false)
      notifyPaymentRegistrationError(t)
    },
  })
  const { deleteAssociations } = useDeleteBankLineSubjectAssociations()
  const { invalidateInvoices, invalidateInvoice } = useInvalidateInvoices()

  const reconcileSubject = () => {
    if (!organizationId || !bankLineId || !invoiceId) {
      return
    }

    setIsProcessing(true)

    const associationIds = invoiceSubjectAssociations.map(({ id }) => id)

    deleteAssociations(associationIds, {
      onSuccess: () => {
        createInvoicesAndBankLinesReconciliation({
          bankLinesIds: [bankLineId],
          organizationId,
          subjects: convertSubjectsIdsToReconciliationSubjects([invoiceId], invoiceType),
          parameters: {
            differenceType,
            ...(crossCurrencyPaymentAmount && { expectedAmount: crossCurrencyPaymentAmount }),
            ...(isAutoMatch && { isAutoMatch }),
          },
          uiSource,
          uiFlow,
        })
      },
    })
  }

  return {
    reconcileSubject,
    isProcessing,
  }
}
